import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { useCreateWork, useUpdateWork, useWorkFacets } from './useWorks';
import { useWorkTypes, useTreeSpecies } from '@/features/regions/useRegions';
import type { WorkRecordDto, WorkRecordCreateInput } from '@forest/contracts';

const CONTRIB = 0.24;
const OH1 = 0.1;
const OH2 = 0.045;
const lei = new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 });

type FormState = {
  year: string;
  month: string;
  quarter: string;
  planFapt: 'PLAN' | 'FAPT';
  entitateSilvica: string;
  entityUp: string;
  primarieComposite: string;
  codPrimarie: string;
  parcela: string;
  subparcela: string;
  ucf: string;
  ucfPlus: string;
  workType: string;
  baseSpecies: string;
  placementScheme: string;
  fundingSource: string;
  plannedAreaHa: string;
  totalAreaHa: string;
  wages: string;
  fuel: string;
  services: string;
  otherCosts: string;
  plantingMaterialSum: string;
};

function fromDto(w?: WorkRecordDto | null): FormState {
  const s = (v: string | number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  return {
    year: s(w?.year ?? new Date().getFullYear()),
    month: s(w?.month),
    quarter: s(w?.quarter),
    planFapt: w?.planFapt ?? 'FAPT',
    entitateSilvica: s(w?.entitateSilvica),
    entityUp: s(w?.entityUp ?? w?.ocolSilvic),
    primarieComposite: s(w?.primarieComposite),
    codPrimarie: s(w?.codPrimarie),
    parcela: s(w?.parcela),
    subparcela: s(w?.subparcela),
    ucf: s(w?.ucf),
    ucfPlus: s(w?.ucfPlus),
    workType: s(w?.workType),
    baseSpecies: s(w?.baseSpecies),
    placementScheme: s(w?.placementScheme),
    fundingSource: s(w?.fundingSource),
    plannedAreaHa: s(w?.plannedAreaHa),
    totalAreaHa: s(w?.totalAreaHa),
    wages: s(w?.wages),
    fuel: s(w?.fuel),
    services: s(w?.services),
    otherCosts: s(w?.otherCosts),
    plantingMaterialSum: s(w?.plantingMaterialSum),
  };
}

const numOrNull = (v: string): number | null => {
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export function WorkFormDialog({
  open,
  onClose,
  work,
}: {
  open: boolean;
  onClose: () => void;
  work?: WorkRecordDto | null;
}) {
  const { t } = useTranslation(['works', 'common']);
  const pushToast = useUiStore((s) => s.pushToast);
  const createMut = useCreateWork();
  const updateMut = useUpdateWork();
  const workTypesQ = useWorkTypes();
  const speciesQ = useTreeSpecies();
  const facetsQ = useWorkFacets();
  const isEdit = !!work;
  const [f, setF] = useState<FormState>(() => fromDto(work));

  // Reset the form each time the dialog opens for a different record.
  const key = work?.id ?? 'new';
  const [lastKey, setLastKey] = useState(key);
  if (open && lastKey !== key) {
    setLastKey(key);
    setF(fromDto(work));
  }

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  const costs = useMemo(() => {
    const wages = numOrNull(f.wages) ?? 0;
    const contributions = Math.round(wages * CONTRIB * 100) / 100;
    const totalDirect =
      Math.round(
        (wages +
          contributions +
          (numOrNull(f.fuel) ?? 0) +
          (numOrNull(f.services) ?? 0) +
          (numOrNull(f.otherCosts) ?? 0) +
          (numOrNull(f.plantingMaterialSum) ?? 0)) *
          100,
      ) / 100;
    const overhead1 = Math.round(totalDirect * OH1 * 100) / 100;
    const overhead2 = Math.round(totalDirect * OH2 * 100) / 100;
    const totalFinal = Math.round((totalDirect + overhead1 + overhead2) * 100) / 100;
    return { contributions, totalDirect, overhead1, overhead2, totalFinal };
  }, [f.wages, f.fuel, f.services, f.otherCosts, f.plantingMaterialSum]);

  const submit = async () => {
    const year = numOrNull(f.year);
    if (!year) {
      pushToast({ variant: 'error', message: t('works:filters.year') });
      return;
    }
    const input: WorkRecordCreateInput = {
      year,
      month: numOrNull(f.month),
      quarter: numOrNull(f.quarter),
      planFapt: f.planFapt,
      entitateSilvica: f.entitateSilvica || null,
      entityUp: f.entityUp || null,
      ocolSilvic: f.entityUp || null,
      primarieComposite: f.primarieComposite || null,
      codPrimarie: f.codPrimarie || null,
      parcela: f.parcela || null,
      subparcela: f.subparcela || null,
      parcelaPlus: f.parcela && f.subparcela ? `${f.parcela}${f.subparcela}` : null,
      ucf: f.ucf || null,
      // UCF+ = UCF + sub-parcel letter (matches the fp sheet's generated code).
      ucfPlus: f.ucfPlus || (f.ucf && f.subparcela ? `${f.ucf}${f.subparcela}` : f.ucf || null),
      workType: f.workType || null,
      baseSpecies: f.baseSpecies || null,
      placementScheme: f.placementScheme || null,
      fundingSource: f.fundingSource || null,
      plannedAreaHa: numOrNull(f.plannedAreaHa),
      totalAreaHa: numOrNull(f.totalAreaHa),
      wages: numOrNull(f.wages),
      fuel: numOrNull(f.fuel),
      services: numOrNull(f.services),
      otherCosts: numOrNull(f.otherCosts),
      plantingMaterialSum: numOrNull(f.plantingMaterialSum),
    };
    try {
      if (isEdit && work) await updateMut.mutateAsync({ id: work.id, input });
      else await createMut.mutateAsync(input);
      pushToast({ variant: 'success', message: t('common:state.saved', { defaultValue: 'Salvat' }) });
      onClose();
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? t('works:form.editTitle') : t('works:form.newTitle')}>
      <div className="max-h-[70vh] space-y-4 overflow-y-auto p-1">
        <Section title={t('works:form.period')}>
          <Field label={t('works:filters.year')}><NumInput value={f.year} onChange={set('year')} /></Field>
          <Field label={t('works:filters.month')}><NumInput value={f.month} onChange={set('month')} /></Field>
          <Field label={t('works:filters.quarter')}><NumInput value={f.quarter} onChange={set('quarter')} /></Field>
          <Field label={t('works:filters.planFapt')}>
            <select value={f.planFapt} onChange={set('planFapt')} className={inputCls}>
              <option value="PLAN">{t('works:planFapt.PLAN')}</option>
              <option value="FAPT">{t('works:planFapt.FAPT')}</option>
            </select>
          </Field>
        </Section>

        <Section title={t('works:form.location')}>
          <Field label={t('works:filters.entity')}><TxtInput value={f.entitateSilvica} onChange={set('entitateSilvica')} /></Field>
          <Field label={t('works:filters.ocol')}><TxtInput value={f.entityUp} onChange={set('entityUp')} /></Field>
          <Field label={t('works:table.primarie')}><TxtInput value={f.primarieComposite} onChange={set('primarieComposite')} /></Field>
          <Field label="APL"><TxtInput value={f.codPrimarie} onChange={set('codPrimarie')} /></Field>
          <Field label="Parcela"><TxtInput value={f.parcela} onChange={set('parcela')} /></Field>
          <Field label="Sub.parcela"><TxtInput value={f.subparcela} onChange={set('subparcela')} /></Field>
          <Field label="UCF"><TxtInput value={f.ucf} onChange={set('ucf')} /></Field>
          <Field label="UCF+"><TxtInput value={f.ucfPlus} onChange={set('ucfPlus')} /></Field>
        </Section>

        <Section title={t('works:form.work')}>
          <Field label={t('works:filters.workType')} wide>
            <select value={f.workType} onChange={set('workType')} className={inputCls}>
              <option value="">—</option>
              {/* Keep the current value even if it's not (yet) in the reference list. */}
              {f.workType && !(workTypesQ.data?.items ?? []).some((w) => w.name === f.workType) && (
                <option value={f.workType}>{f.workType}</option>
              )}
              {(workTypesQ.data?.items ?? []).map((w) => (
                <option key={w.id} value={w.name}>{w.name}</option>
              ))}
            </select>
          </Field>
          <Field label={t('works:form.baseSpecies', { defaultValue: 'Specia de bază' })}>
            <select value={f.baseSpecies} onChange={set('baseSpecies')} className={inputCls}>
              <option value="">—</option>
              {f.baseSpecies && !(speciesQ.data?.items ?? []).some((s) => s.cod === f.baseSpecies) && (
                <option value={f.baseSpecies}>{f.baseSpecies}</option>
              )}
              {(speciesQ.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.cod}>{s.name ? `${s.cod} — ${s.name}` : s.cod}</option>
              ))}
            </select>
          </Field>
          <Field label={t('works:form.scheme', { defaultValue: 'Schema de amplasare' })}><TxtInput value={f.placementScheme} onChange={set('placementScheme')} placeholder="2,5 x 0,7" /></Field>
          <Field label={t('works:filters.funding')}>
            <input list="work-funding-list" value={f.fundingSource} onChange={set('fundingSource')} className={inputCls} />
            <datalist id="work-funding-list">
              {(facetsQ.data?.fundingSources ?? []).map((fs) => <option key={fs} value={fs} />)}
            </datalist>
          </Field>
          <Field label={t('works:form.plannedArea')}><NumInput value={f.plannedAreaHa} onChange={set('plannedAreaHa')} /></Field>
          <Field label={t('works:form.totalArea')}><NumInput value={f.totalAreaHa} onChange={set('totalAreaHa')} /></Field>
        </Section>

        <Section title={t('works:form.costs')}>
          <Field label={t('works:table.wages')}><NumInput value={f.wages} onChange={set('wages')} /></Field>
          <Field label={t('works:form.fuel')}><NumInput value={f.fuel} onChange={set('fuel')} /></Field>
          <Field label={t('works:form.services')}><NumInput value={f.services} onChange={set('services')} /></Field>
          <Field label={t('works:form.other')}><NumInput value={f.otherCosts} onChange={set('otherCosts')} /></Field>
          <Field label={t('works:form.material')}><NumInput value={f.plantingMaterialSum} onChange={set('plantingMaterialSum')} /></Field>
        </Section>

        <div className="rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800/60">
          <div className="mb-1 text-xs font-semibold uppercase text-slate-500">{t('works:form.computed')}</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
            <Computed label={t('works:form.contributions')} value={costs.contributions} />
            <Computed label={t('works:form.totalDirect')} value={costs.totalDirect} />
            <Computed label={t('works:form.overhead1')} value={costs.overhead1} />
            <Computed label={t('works:form.overhead2')} value={costs.overhead2} />
            <Computed label={t('works:table.total')} value={costs.totalFinal} bold />
          </div>
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel', { defaultValue: 'Anulează' })}</Button>
        <Button onClick={submit} loading={saving} disabled={saving}>
          {t('common:actions.save', { defaultValue: 'Salvează' })}
        </Button>
      </div>
    </Dialog>
  );
}

const inputCls = 'w-full rounded border border-slate-300 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-800';

function TxtInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input type="text" {...props} className={inputCls} />;
}
function NumInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input type="text" inputMode="decimal" {...props} className={`${inputCls} text-right tabular-nums`} />;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase text-slate-500">{title}</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">{children}</div>
    </div>
  );
}
function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? 'col-span-2 sm:col-span-4' : ''}>
      <label className="mb-0.5 block text-[11px] text-slate-500">{label}</label>
      {children}
    </div>
  );
}
function Computed({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className={`tabular-nums ${bold ? 'font-bold text-forest-700' : 'text-slate-700 dark:text-slate-200'}`}>{lei.format(value)}</span>
    </div>
  );
}
